/**
 * Filtering the Help Centre.
 *
 * A filter, not a search engine, and deliberately so. The corpus is nineteen
 * sections that already live in the bundle, so there is nothing to fetch, index
 * or rank: every word of the query must appear somewhere in the section, in any
 * order, and the sections that survive stay in the order the page writes them.
 * No fuzzy matching — on a corpus this size it buys nothing and makes "why did
 * that come up?" unanswerable.
 *
 * It exists because the accordion unmounts closed panels (ui/Accordion), so the
 * browser's own find-on-page can only ever see the section already open. This
 * searches the content rather than the DOM, which is the only way to find a word
 * inside a section nobody has opened yet.
 *
 * Pure functions, no React: the matching rules are unit-tested in search.test.ts
 * rather than driven through a rendered page.
 */

import type { HelpBlock, HelpSection } from './content'
import { segments } from './emphasis'

/** A half-open [start, end) range over a string. */
export type Span = [number, number]

export type IndexedSection = {
  section: HelpSection
  /** The section as a reader sees it, one entry per title, paragraph or item. */
  lines: string[]
  /** Every line folded and joined, so a match test is one substring scan. */
  folded: string
}

/**
 * Case and accents removed, so "Pause" finds "pause" and a pasted "café" finds
 * "cafe".
 *
 * Highlighting indexes back into the *unfolded* string, which holds because
 * decomposing a precomposed letter and then dropping its mark returns a single
 * character again. A source string that arrived already decomposed would shift
 * those offsets; the help content is written precomposed, and is ASCII today.
 */
export function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

/** The visible text of one block, with the bold delimiters taken out. */
function blockLines(block: HelpBlock): string[] {
  const plain = (body: string) =>
    segments(body)
      .map((s) => s.text)
      .join('')

  if (block.kind === 'list') {
    return block.items.map((item) => [item.term, plain(item.body)].filter(Boolean).join(' — '))
  }
  return [plain(block.body)]
}

export function buildIndex(sections: readonly HelpSection[]): IndexedSection[] {
  return sections.map((section) => {
    const lines = [section.title, ...section.blocks.flatMap(blockLines)]
    return { section, lines, folded: fold(lines.join('\n')) }
  })
}

/**
 * The query as words. Punctuation is left alone: "auto-archive" is one word to
 * a reader, and splitting it would quietly widen the match to every "archive".
 */
export function tokenize(query: string): string[] {
  return fold(query).split(/\s+/).filter(Boolean)
}

export function filterSections(index: readonly IndexedSection[], tokens: readonly string[]): IndexedSection[] {
  if (!tokens.length) return [...index]
  return index.filter((entry) => tokens.every((token) => entry.folded.includes(token)))
}

/**
 * Where every token occurs in a string, merged so overlapping hits mark once.
 * Overlap is not hypothetical: "deal deals" would otherwise open two marks over
 * the same letters and render nested.
 */
export function matchSpans(text: string, tokens: readonly string[]): Span[] {
  if (!tokens.length) return []
  const folded = fold(text)
  const found: Span[] = []

  for (const token of tokens) {
    let at = folded.indexOf(token)
    while (at !== -1) {
      found.push([at, at + token.length])
      at = folded.indexOf(token, at + token.length)
    }
  }

  found.sort((a, b) => a[0] - b[0])
  const merged: Span[] = []
  for (const span of found) {
    const last = merged[merged.length - 1]
    if (last && span[0] <= last[1]) last[1] = Math.max(last[1], span[1])
    else merged.push([...span])
  }
  return merged
}

/** A run of text carrying both of the things that can be true of it. */
export type Piece = { text: string; strong: boolean; mark: boolean }

/**
 * One pass that resolves bold and highlighting together. They are independent —
 * a query can land in the middle of a bold phrase — so the pieces are the bold
 * segments cut again at every match boundary.
 */
export function pieces(body: string, tokens: readonly string[]): Piece[] {
  const out: Piece[] = []

  for (const segment of segments(body)) {
    const spans = matchSpans(segment.text, tokens)
    let at = 0
    for (const [start, end] of spans) {
      if (start > at) out.push({ text: segment.text.slice(at, start), strong: segment.strong, mark: false })
      out.push({ text: segment.text.slice(start, end), strong: segment.strong, mark: true })
      at = end
    }
    if (at < segment.text.length) out.push({ text: segment.text.slice(at), strong: segment.strong, mark: false })
  }

  return out
}

/** How much of the line either side of the first hit a snippet carries. */
const LEAD = 46
const TRAIL = 92

/**
 * One line of context around the first hit: the reason this section survived the
 * query, in the words that matched.
 *
 * Taken from within a single line, so a snippet never welds the end of one
 * bullet onto the start of the next, and trimmed at word boundaries so it does
 * not cut a word in half.
 */
export function snippet(entry: IndexedSection, tokens: readonly string[]): string {
  const line = entry.lines.find((candidate) => matchSpans(candidate, tokens).length)
  const hit = line ? matchSpans(line, tokens)[0] : undefined
  if (!line || !hit) return ''

  const [hitStart, hitEnd] = hit
  const from = Math.max(0, hitStart - LEAD)
  const start = from === 0 ? 0 : line.indexOf(' ', from) + 1
  const to = Math.min(line.length, hitEnd + TRAIL)
  const end = to === line.length ? to : line.lastIndexOf(' ', to)

  return `${start > 0 ? '…' : ''}${line.slice(start, end).trim()}${end < line.length ? '…' : ''}`
}
